import React, { Suspense, lazy, useState, useEffect, useCallback } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { ConfigProvider, App as AntApp, Spin } from 'antd';
import { Navigate } from 'react-router-dom';
import frFR from 'antd/locale/fr_FR';
import { AuthProvider } from './context/AuthContext';
import Layout from './components/layout/Layout';
import AdminLayout from './components/layout/AdminLayout';
import Home from './pages/Home';
import ScrollToTop from './components/shared/ScrollToTop';
import WhatsAppButton from './components/shared/WhatsAppButton';
import ServerOffline from './pages/ServerOffline';
import { onServerDown, isServerDown, resetServerDown } from './api/axios';

const About = lazy(() => import('./pages/About'));
const ServiceDetail = lazy(() => import('./pages/ServiceDetail'));
const HadjOumra = lazy(() => import('./pages/HadjOumra'));
const NavetteAibd = lazy(() => import('./pages/NavetteAibd'));
const LocationVehicules = lazy(() => import('./pages/LocationVehicules'));
const Assurance = lazy(() => import('./pages/Assurance'));
const Conseils = lazy(() => import('./pages/Conseils'));
const Gallery = lazy(() => import('./pages/Gallery'));
const Actualites = lazy(() => import('./pages/Actualites'));
const ActualiteDetail = lazy(() => import('./pages/ActualiteDetail'));
const Contact = lazy(() => import('./pages/Contact'));
const Devis = lazy(() => import('./pages/Devis'));
const Login = lazy(() => import(/* webpackPrefetch: true */ './pages/Login'));
const Register = lazy(() => import(/* webpackPrefetch: true */ './pages/Register'));
const Dashboard = lazy(() => import('./pages/admin/Dashboard'));
const ManageActualites = lazy(() => import('./pages/admin/ManageActualites'));
const ManageContacts = lazy(() => import('./pages/admin/ManageContacts'));
const ManageDevis = lazy(() => import('./pages/admin/ManageDevis'));

const theme = {
  token: {
    colorPrimary: '#1e3a5f',
    colorSuccess: '#00b894',
    colorWarning: '#d4af37',
    colorError: '#e74c3c',
    fontFamily: "'Poppins', sans-serif",
    borderRadius: 8,
    fontSize: 14,
    colorBgContainer: '#ffffff',
  },
  components: {
    Button: { colorPrimary: '#1e3a5f', algorithm: true },
    Menu: { colorItemBgSelected: 'rgba(0,184,148,0.1)' },
    Card: { borderRadiusLG: 12 },
  },
};

const AppContent = () => {
  const location = useLocation();
  const isAdmin = location.pathname.startsWith('/admin');
  const [serverOffline, setServerOffline] = useState(isServerDown());

  const handleServerStatus = useCallback((down) => {
    setServerOffline(down);
  }, []);

  const handleRetry = useCallback(() => {
    resetServerDown();
    setServerOffline(false);
    window.location.reload();
  }, []);

  useEffect(() => {
    onServerDown(handleServerStatus);
    setServerOffline(isServerDown());
  }, [handleServerStatus]);

  if (serverOffline) {
    return <ServerOffline onRetry={handleRetry} />;
  }

  return (
    <>
      <ScrollToTop />
      {!isAdmin && <WhatsAppButton />}
      <Suspense fallback={<div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}><Spin size="large" /></div>}>
        <Routes>
        <Route path="/" element={<Layout />}>
          <Route index element={<Home />} />
          <Route path="a-propos" element={<About />} />
          <Route path="services" element={<Navigate to="/" replace />} />
          <Route path="services/navette-aibd" element={<NavetteAibd />} />
          <Route path="services/location-vehicules" element={<LocationVehicules />} />
          <Route path="services/assurance" element={<Assurance />} />
          <Route path="services/conseils" element={<Conseils />} />
          <Route path="services/:slug" element={<ServiceDetail />} />
          <Route path="hadj-oumra" element={<HadjOumra />} />
          <Route path="galerie" element={<Gallery />} />
          <Route path="actualites" element={<Actualites />} />
          <Route path="actualites/:slug" element={<ActualiteDetail />} />
          <Route path="contact" element={<Contact />} />
          <Route path="demander-devis" element={<Devis />} />
        </Route>
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />
        <Route path="/admin/*" element={<AdminLayout />}>
          <Route index element={<Dashboard />} />
          <Route path="actualites" element={<ManageActualites />} />
          <Route path="contacts" element={<ManageContacts />} />
          <Route path="devis" element={<ManageDevis />} />
        </Route>
      </Routes>
      </Suspense>
    </>
  );
};

function App() {
  return (
    <ConfigProvider theme={theme} locale={frFR}>
      <AntApp>
        <AuthProvider>
          <Router>
            <AppContent />
          </Router>
        </AuthProvider>
      </AntApp>
    </ConfigProvider>
  );
}

export default App;
